import type { PlayerAIToolProposalPayload } from "./playerai.types";

/**
 * How a Proposal's lifecycle status is spelled and coloured.
 *
 * Shared by the generic `ToolProposalWidget` and by every dedicated card, so it
 * lives in neither: a status pill is a property of a Proposal, not of the
 * Hoshin workshop that happens to render one. Keeping it here also stops the
 * generic widget importing a module-specific file for a generic concern.
 */
export const PROPOSAL_STATUS_LABEL: Record<
  PlayerAIToolProposalPayload["status"],
  string
> = {
  pending: "Pending",
  applying: "Applying",
  rejected: "Rejected",
  expired: "Expired",
  applied: "Applied",
  failed: "Failed",
  stale: "Stale",
};

export const PROPOSAL_STATUS_CLASS: Record<
  PlayerAIToolProposalPayload["status"],
  string
> = {
  pending: "border-pb-amber/40 bg-pb-amber-soft text-pb-amber-text",
  applying: "border-pb-cyan/40 bg-pb-cyan-soft text-pb-cyan-text",
  rejected: "border-pb-border bg-pb-card text-pb-text-muted",
  expired: "border-pb-border bg-pb-card text-pb-text-muted",
  applied: "border-pb-green/40 bg-pb-green-soft text-pb-green-text",
  failed: "border-pb-red/40 bg-pb-red-soft text-pb-red-text",
  stale: "border-pb-amber/40 bg-pb-amber-soft text-pb-amber-text",
};

/**
 * Where an APPLIED proposal's canvas writes stand, from its saved execution
 * result: `failed` or `pending` when the product accepted the change but the
 * canvas has not confirmed it, `confirmed` otherwise (including every
 * proposal that was not applied).
 */
export function proposalCanvasDeliveryState(
  payload: PlayerAIToolProposalPayload,
): "confirmed" | "pending" | "failed" {
  const states: unknown[] = [];
  const collect = (value: unknown): void => {
    if (typeof value !== "object" || value === null || Array.isArray(value))
      return;
    const row = value as Record<string, unknown>;
    if (
      typeof row.canvas_delivery === "object" &&
      row.canvas_delivery !== null
    ) {
      states.push((row.canvas_delivery as Record<string, unknown>).state);
    }
    if (row.canvas_sync_status !== undefined)
      states.push(row.canvas_sync_status);
  };
  const result = payload.executionResult;
  collect(result);
  collect(result?.result);
  const body =
    result?.result && typeof result.result === "object"
      ? (result.result as Record<string, unknown>)
      : result;
  if (Array.isArray(body?.changes)) body.changes.forEach(collect);
  if (payload.status === "applied" && states.length > 0) {
    if (states.includes("failed")) return "failed";
    if (states.some((state) => state !== "applied" && state !== "delivered"))
      return "pending";
  }
  return "confirmed";
}

/** Product acceptance is not confirmation that a queued canvas write was saved. */
export function proposalDisplayStatus(payload: PlayerAIToolProposalPayload): {
  label: string;
  className: string;
} {
  const delivery = proposalCanvasDeliveryState(payload);
  if (delivery === "failed")
    return {
      label: "Canvas update failed",
      className: PROPOSAL_STATUS_CLASS.failed,
    };
  if (delivery === "pending")
    return {
      label: "Canvas update pending",
      className: PROPOSAL_STATUS_CLASS.pending,
    };
  return {
    label: PROPOSAL_STATUS_LABEL[payload.status],
    className: PROPOSAL_STATUS_CLASS[payload.status],
  };
}
